import { useSelector } from 'react-redux';
import { createStateSelector } from '~/_shared/utils/memoize/createSelector';
import { memoizeWeak } from '~/_shared/utils/memoize/memoize';
import { numberObjectKeys } from '~/_shared/utils/object/objectEnumerators';
import { type AppState } from '~/store/app.store';
import { type BoundaryGroupId } from '~/store/boundaries/boundaryIdentifier.type';
import { boundaryToolActiveBTGsSelector } from '~/store/boundaryTerritoryGroups/boundaryTool/boundaryToolBTGs.selectors';
import {
  boundaryFiltersStateSelector, boundaryLocationsFiltersStateSelector,
} from '~/store/mapSettings/toolsState/boundary/mapSettingsToolsStateBoundary.stateSelectors';
import { boundaryFiltersStateToBoundaryFilters } from '~/store/mapSettings/toolsState/boundaryTerritoryGroups/mapSettingsBTGToolsState.helpers';
import { type BoundaryFiltersGeneric } from '~/store/mapSettings/toolsState/boundaryTerritoryGroups/mapSettingsBTGToolsState.selectors';
import { mapSettingsBoundariesIsolatedBoundariesSelector } from '../../boundaries/mapSettingsBoundaries.selectors';
import {
  type BoundaryLocationsFilter,
  type BoundaryLocationsFiltersState,
} from './mapSettingsToolsStateBoundary.state';

export const emptyBoundaryLocationsFilter: BoundaryLocationsFilter = {
  filteredBoundaries: [],
  filteredBoundaryTerritories: [],
};

export type BoundaryLocationsFilters = Readonly<{
  all: BoundaryFiltersGeneric<BoundaryLocationsFilter>;
  isolated: BoundaryFiltersGeneric<BoundaryLocationsFilter>;
}>;

// Boundary Filters Selectors
export const boundaryToolFiltersSelector = createStateSelector([
  boundaryFiltersStateSelector,
  boundaryToolActiveBTGsSelector,
], boundaryFiltersStateToBoundaryFilters);

export const useBoundaryToolFilters = () => useSelector(boundaryToolFiltersSelector);

// Boundary Locations Filters Selectors
const boundaryLocationsFiltersSelector = (state: AppState): BoundaryLocationsFilters =>
  getBoundaryTerritoryGroupLocationsFiltersMemoized(
    boundaryLocationsFiltersStateSelector(state),
    mapSettingsBoundariesIsolatedBoundariesSelector(state),
  );

export const useBoundaryLocationsFilters = () =>
  useSelector(boundaryLocationsFiltersSelector);

export const getBoundaryTerritoryGroupLocationsFiltersMemoized = memoizeWeak((
  locationsFilter: BoundaryLocationsFiltersState,
  isolatedBoundaries: BoundaryLocationsFiltersState,
): BoundaryLocationsFilters => {
  const allBoundaryGroupIds = [...numberObjectKeys(locationsFilter), ...numberObjectKeys(isolatedBoundaries)];
  const combinedFilters = allBoundaryGroupIds.reduce((acc, boundaryGroupId) => ({
    ...acc,
    [boundaryGroupId]: {
      ...acc[boundaryGroupId],
      filteredBoundaries: [
        ...(locationsFilter[boundaryGroupId]?.filteredBoundaries ?? []),
        ...(isolatedBoundaries[boundaryGroupId]?.filteredBoundaries ?? []),
      ],
      filteredBoundaryTerritories: [
        ...(locationsFilter[boundaryGroupId]?.filteredBoundaryTerritories ?? []),
        ...(isolatedBoundaries[boundaryGroupId]?.filteredBoundaryTerritories ?? []),
      ],
    },
  }), {} as BoundaryLocationsFiltersState);

  const toEntries = (filters: BoundaryLocationsFiltersState) => Object.entries(filters)
    .filter((pair): pair is [string, BoundaryLocationsFilter] => {
      const [_, filter] = pair;
      return !!filter && (!!filter.filteredBoundaries.length || !!filter.filteredBoundaryTerritories.length);
    })
    .map(([key, filter]) => ({
      boundaryGroupId: parseInt(key, 10),
      filter: filter satisfies BoundaryLocationsFilter,
    }));

  return {
    all: {
      get: (boundaryGroupId: BoundaryGroupId) => combinedFilters[boundaryGroupId] ?? emptyBoundaryLocationsFilter,
      entries: toEntries(combinedFilters),
    },
    isolated: {
      get: (boundaryGroupId: BoundaryGroupId) => isolatedBoundaries[boundaryGroupId] ?? emptyBoundaryLocationsFilter,
      entries: toEntries(isolatedBoundaries),
    },
  };
});
